import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  CircularProgress,
  InputAdornment,
  Toolbar,
  Select,
  FormControl,
  InputLabel,
  Divider,
  Rating,
  Stack,
  GridLegacy as Grid,
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  Search as SearchIcon,
  Feedback as FeedbackIcon,
  Reply as ReplyIcon,
  Delete as DeleteIcon,
  CheckCircle as ResolveIcon,
  Cancel as CloseIcon,
  Refresh as RefreshIcon,
  ThumbUp,
  ThumbDown,
  BugReport,
  EmojiObjects,
  Star,
  TrendingUp,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  serverTimestamp,
  getDoc,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';

interface FeedbackItem {
  id: string;
  type: 'feedback' | 'suggestion' | 'bug' | 'feature';
  category: string;
  title: string;
  description: string;
  rating?: number;
  priority: 'low' | 'medium' | 'high';
  sentiment: 'positive' | 'neutral' | 'negative';
  status: 'open' | 'in-progress' | 'resolved' | 'closed';
  timestamp: any;
  userId?: string;
  userEmail?: string;
  userName: string;
  votes: {
    up: number;
    down: number;
    total: number;
  };
  adminResponse?: string;
  respondedAt?: any;
  respondedBy?: string;
  resolvedAt?: any;
  resolvedBy?: string;
}

export const AdminFeedback: React.FC = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState<FeedbackItem[]>([]);
  const [filteredFeedback, setFilteredFeedback] = useState<FeedbackItem[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedFeedback, setSelectedFeedback] = useState<FeedbackItem | null>(null);
  const [viewDialog, setViewDialog] = useState(false);
  const [responseDialog, setResponseDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [responseText, setResponseText] = useState('');

  // Check admin status
  useEffect(() => {
    const checkAccess = async () => {
      if (!currentUser) {
        navigate('/login');
        return;
      }

      try {
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          const isAdmin = userData.isAdmin === true;
          
          if (!isAdmin) {
            setError('Access denied. Admin privileges required.');
            setTimeout(() => navigate('/dashboard'), 2000);
            return;
          }
        } else {
          setError('User profile not found.');
          setTimeout(() => navigate('/dashboard'), 2000);
          return;
        }
      } catch (error) {
        console.error('Error checking admin status:', error);
        setError('Failed to verify admin status.');
        setTimeout(() => navigate('/dashboard'), 2000);
        return;
      }

      setLoading(false);
    };

    checkAccess();
  }, [currentUser, navigate]);

  // Load feedback
  const loadFeedback = async () => {
    if (!currentUser) return;
    
    try {
      setLoading(true);
      const feedbackQuery = query(
        collection(db, 'feedback'), 
        orderBy('timestamp', 'desc')
      );
      const feedbackSnapshot = await getDocs(feedbackQuery);
      const feedbackData: FeedbackItem[] = [];

      feedbackSnapshot.forEach((doc) => {
        const data = doc.data();
        feedbackData.push({
          id: doc.id,
          ...data,
        } as FeedbackItem);
      });

      setFeedback(feedbackData);
      setFilteredFeedback(feedbackData);
    } catch (error) {
      console.error('Error loading feedback:', error);
      setError('Failed to load feedback');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFeedback();
  }, [currentUser]);

  // Filter feedback
  useEffect(() => {
    let filtered = feedback;

    if (searchTerm) {
      filtered = filtered.filter(
        (item) =>
          item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.userName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.category.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (filterType !== 'all') {
      filtered = filtered.filter((item) => item.type === filterType);
    }

    if (filterStatus !== 'all') {
      filtered = filtered.filter((item) => item.status === filterStatus);
    }

    if (filterPriority !== 'all') {
      filtered = filtered.filter((item) => item.priority === filterPriority);
    }

    setFilteredFeedback(filtered);
    setPage(0);
  }, [feedback, searchTerm, filterType, filterStatus, filterPriority]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, item: FeedbackItem) => {
    setAnchorEl(event.currentTarget);
    setSelectedFeedback(item);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedFeedback(null);
  };

  const handleViewFeedback = () => {
    setViewDialog(true);
    setAnchorEl(null);
  };

  const handleRespondToFeedback = () => {
    setResponseDialog(true);
    setResponseText('');
    setAnchorEl(null);
  };

  const handleUpdateStatus = async (status: string) => {
    if (!selectedFeedback) return;

    try {
      const updateData: any = {
        status,
      };

      if (status === 'resolved') {
        updateData.resolvedAt = serverTimestamp();
        updateData.resolvedBy = currentUser?.displayName || currentUser?.email || 'Admin';
      }

      await updateDoc(doc(db, 'feedback', selectedFeedback.id), updateData);
      
      setSuccess(`Feedback marked as ${status}`);
      loadFeedback();
    } catch (error) {
      console.error('Error updating status:', error);
      setError('Failed to update status');
    }
    handleMenuClose();
  };

  const handleSendResponse = async () => {
    if (!selectedFeedback || !responseText.trim()) return;

    try {
      await updateDoc(doc(db, 'feedback', selectedFeedback.id), {
        status: 'in-progress',
        adminResponse: responseText.trim(),
        respondedAt: serverTimestamp(),
        respondedBy: currentUser?.displayName || currentUser?.email || 'Admin',
      });
      
      setSuccess('Response saved successfully');
      setResponseDialog(false);
      setResponseText('');
      loadFeedback();
    } catch (error) {
      console.error('Error saving response:', error);
      setError('Failed to save response');
    }
  };

  const handleDeleteFeedback = async () => {
    if (!selectedFeedback) return;

    try {
      await deleteDoc(doc(db, 'feedback', selectedFeedback.id));
      setSuccess('Feedback deleted successfully');
      setDeleteDialog(false);
      loadFeedback();
    } catch (error) {
      console.error('Error deleting feedback:', error);
      setError('Failed to delete feedback');
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'feedback': return <FeedbackIcon />;
      case 'suggestion': return <EmojiObjects />;
      case 'bug': return <BugReport />;
      case 'feature': return <Star />;
      default: return <FeedbackIcon />;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'feedback': return 'primary';
      case 'suggestion': return 'info';
      case 'bug': return 'error';
      case 'feature': return 'warning';
      default: return 'default';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open': return 'error';
      case 'in-progress': return 'warning';
      case 'resolved': return 'success';
      case 'closed': return 'default';
      default: return 'default';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  const getSentimentIcon = (sentiment: string) => {
    switch (sentiment) {
      case 'positive': return <ThumbUp color="success" />;
      case 'negative': return <ThumbDown color="error" />;
      default: return null;
    }
  };

  const formatTimestamp = (timestamp: any) => {
    if (!timestamp) return 'Unknown';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return format(date, 'MMM dd, yyyy HH:mm');
  };

  const getStats = () => {
    const stats = {
      total: feedback.length,
      open: feedback.filter(f => f.status === 'open').length,
      inProgress: feedback.filter(f => f.status === 'in-progress').length,
      resolved: feedback.filter(f => f.status === 'resolved').length,
      bugs: feedback.filter(f => f.type === 'bug').length,
      features: feedback.filter(f => f.type === 'feature').length,
      suggestions: feedback.filter(f => f.type === 'suggestion').length,
      general: feedback.filter(f => f.type === 'feedback').length,
    };
    return stats;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  const stats = getStats();

  return (
    <Box sx={{ minHeight: '100vh', pt: { xs: 10, md: 12 }, pb: 4 }}>
      <Container maxWidth="xl">
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            💬 Feedback Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage user feedback, suggestions, and bug reports
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {/* Statistics Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h4" color="primary">{stats.total}</Typography>
                <Typography variant="body2" color="text.secondary">Total Feedback</Typography>
              </Box>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h4" color="error">{stats.open}</Typography>
                <Typography variant="body2" color="text.secondary">Open Items</Typography>
              </Box>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h4" color="warning">{stats.inProgress}</Typography>
                <Typography variant="body2" color="text.secondary">In Progress</Typography>
              </Box>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h4" color="success">{stats.resolved}</Typography>
                <Typography variant="body2" color="text.secondary">Resolved</Typography>
              </Box>
            </Card>
          </Grid>
        </Grid>

        <Card sx={{ mb: 3 }}>
          <Toolbar sx={{ gap: 2, flexWrap: 'wrap' }}>
            <TextField
              size="small"
              placeholder="Search feedback..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 250 }}
            />
            
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Type</InputLabel>
              <Select
                value={filterType}
                label="Type"
                onChange={(e) => setFilterType(e.target.value)}
              >
                <MenuItem value="all">All Types</MenuItem>
                <MenuItem value="feedback">Feedback</MenuItem>
                <MenuItem value="suggestion">Suggestions</MenuItem>
                <MenuItem value="bug">Bug Reports</MenuItem>
                <MenuItem value="feature">Features</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={filterStatus}
                label="Status"
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <MenuItem value="all">All Statuses</MenuItem>
                <MenuItem value="open">Open</MenuItem>
                <MenuItem value="in-progress">In Progress</MenuItem>
                <MenuItem value="resolved">Resolved</MenuItem>
                <MenuItem value="closed">Closed</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Priority</InputLabel>
              <Select
                value={filterPriority}
                label="Priority"
                onChange={(e) => setFilterPriority(e.target.value)}
              >
                <MenuItem value="all">All Priorities</MenuItem>
                <MenuItem value="high">High</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="low">Low</MenuItem>
              </Select>
            </FormControl>

            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={loadFeedback}
              sx={{ ml: 'auto' }}
            >
              Refresh
            </Button>
          </Toolbar>
        </Card>

        <Card>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Type</TableCell>
                  <TableCell>Title</TableCell>
                  <TableCell>User</TableCell>
                  <TableCell>Priority</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Submitted</TableCell>
                  <TableCell width={60}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredFeedback
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((item) => (
                    <TableRow key={item.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {getTypeIcon(item.type)}
                          <Chip
                            label={item.type}
                            color={getTypeColor(item.type) as any}
                            size="small"
                          />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="medium" noWrap sx={{ maxWidth: 200 }}>
                            {item.title}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                            <Typography variant="caption" color="text.secondary">
                              {item.category}
                            </Typography>
                            {getSentimentIcon(item.sentiment)}
                            {item.rating && (
                              <Rating value={item.rating} size="small" readOnly />
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {item.userName}
                        </Typography>
                        {item.userEmail && (
                          <Typography variant="caption" color="text.secondary">
                            {item.userEmail}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={item.priority}
                          color={getPriorityColor(item.priority) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={item.status}
                          color={getStatusColor(item.status) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {formatTimestamp(item.timestamp)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuClick(e, item)}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          <TablePagination
            component="div"
            count={filteredFeedback.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        </Card>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={handleViewFeedback}>
            <FeedbackIcon sx={{ mr: 1 }} />
            View Details
          </MenuItem>
          <MenuItem onClick={handleRespondToFeedback}>
            <ReplyIcon sx={{ mr: 1 }} />
            Add Response
          </MenuItem>
          <Divider />
          <MenuItem onClick={() => handleUpdateStatus('in-progress')}>
            <TrendingUp sx={{ mr: 1 }} />
            Mark In Progress
          </MenuItem>
          <MenuItem onClick={() => handleUpdateStatus('resolved')}>
            <ResolveIcon sx={{ mr: 1 }} />
            Mark Resolved
          </MenuItem>
          <MenuItem onClick={() => handleUpdateStatus('closed')}>
            <CloseIcon sx={{ mr: 1 }} />
            Close
          </MenuItem>
          <Divider />
          <MenuItem onClick={() => setDeleteDialog(true)} sx={{ color: 'error.main' }}>
            <DeleteIcon sx={{ mr: 1 }} />
            Delete
          </MenuItem>
        </Menu>

        {/* View Dialog */}
        <Dialog open={viewDialog} onClose={() => { setViewDialog(false); setSelectedFeedback(null); }} maxWidth="md" fullWidth>
          <DialogTitle>
            <Box display="flex" alignItems="center" gap={1}>
              {selectedFeedback && getTypeIcon(selectedFeedback.type)}
              Feedback Details
            </Box>
          </DialogTitle>
          <DialogContent dividers>
            {selectedFeedback && (
              <Box>
                <Stack spacing={3}>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">Title:</Typography>
                    <Typography variant="h6">{selectedFeedback.title}</Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Chip
                      label={selectedFeedback.type}
                      color={getTypeColor(selectedFeedback.type) as any}
                      icon={getTypeIcon(selectedFeedback.type)}
                    />
                    <Chip
                      label={selectedFeedback.status}
                      color={getStatusColor(selectedFeedback.status) as any}
                    />
                    <Chip
                      label={`${selectedFeedback.priority} priority`}
                      color={getPriorityColor(selectedFeedback.priority) as any}
                    />
                    <Chip label={selectedFeedback.category} />
                  </Box>

                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">From:</Typography>
                    <Typography variant="body1">
                      {selectedFeedback.userName} {selectedFeedback.userEmail && `(${selectedFeedback.userEmail})`}
                    </Typography>
                  </Box>
                  
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">Submitted:</Typography>
                    <Typography variant="body2">{formatTimestamp(selectedFeedback.timestamp)}</Typography>
                  </Box>

                  {selectedFeedback.rating && (
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">Rating:</Typography>
                      <Rating value={selectedFeedback.rating} readOnly />
                    </Box>
                  )}

                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">Sentiment:</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {getSentimentIcon(selectedFeedback.sentiment)}
                      <Typography variant="body2">{selectedFeedback.sentiment}</Typography>
                    </Box>
                  </Box>
                  
                  <Divider />
                  
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>Description:</Typography>
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                      {selectedFeedback.description}
                    </Typography>
                  </Box>
                  
                  {selectedFeedback.adminResponse && (
                    <>
                      <Divider />
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          Admin Response:
                        </Typography>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 1 }}>
                          {selectedFeedback.adminResponse}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Responded by {selectedFeedback.respondedBy} on {formatTimestamp(selectedFeedback.respondedAt)}
                        </Typography>
                      </Box>
                    </>
                  )}

                  {selectedFeedback.resolvedAt && (
                    <Box>
                      <Typography variant="caption" color="success.main">
                        Resolved by {selectedFeedback.resolvedBy} on {formatTimestamp(selectedFeedback.resolvedAt)}
                      </Typography>
                    </Box>
                  )}
                </Stack>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { setViewDialog(false); setSelectedFeedback(null); }}>Close</Button>
            <Button variant="contained" onClick={handleRespondToFeedback}>
              Add Response
            </Button>
          </DialogActions>
        </Dialog>

        {/* Response Dialog */}
        <Dialog open={responseDialog} onClose={() => { setResponseDialog(false); setSelectedFeedback(null); }} maxWidth="md" fullWidth>
          <DialogTitle>Respond to Feedback</DialogTitle>
          <DialogContent>
            {selectedFeedback && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Responding to: {selectedFeedback.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  From: {selectedFeedback.userName}
                </Typography>
              </Box>
            )}
            <TextField
              autoFocus
              multiline
              rows={6}
              fullWidth
              label="Your Response"
              value={responseText}
              onChange={(e) => setResponseText(e.target.value)}
              placeholder="Type your response here..."
              helperText="This response will be saved with the feedback item for reference."
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { setResponseDialog(false); setSelectedFeedback(null); }}>Cancel</Button>
            <Button 
              variant="contained" 
              onClick={handleSendResponse}
              disabled={!responseText.trim()}
            >
              Save Response
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Dialog */}
        <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)}>
          <DialogTitle>Delete Feedback</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete this feedback? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialog(false)}>Cancel</Button>
            <Button 
              variant="contained" 
              color="error" 
              onClick={handleDeleteFeedback}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
}; 